import React from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { Loader2, Save } from 'lucide-react';
import { useCreateUser, useUsers } from '@/api/users/use-users';
import { useRoles } from '@/api/roles/use-roles';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { PageContainer, PageHeader } from '@/components/shared/layout';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';

export interface UserFormData {
    name: string;
    title?: string;
    email: string;
    phone?: string;
    whatsapp_number?: string;
    telegram?: string;
    extension?: string;
    password: string;
    password_confirmation: string;
    role_id: string;
    supervisor_id?: string;
}

const UserCreatePage: React.FC = () => {
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();
    const { roles, isLoading: rolesLoading } = useRoles();
    const { data: usersResponse } = useUsers({ status: 'active' });
    const users = usersResponse?.data || [];
    const createUser = useCreateUser();

    const {
        register,
        handleSubmit,
        formState: { errors, isSubmitting },
        setValue,
        watch,
    } = useForm<UserFormData>({
        defaultValues: {
            name: '',
            title: '',
            email: '',
            phone: '',
            whatsapp_number: '',
            telegram: '',
            extension: '',
            password: '',
            password_confirmation: '',
            role_id: '',
            supervisor_id: '',
        },
    });

    const handleFormSubmit = async (data: UserFormData) => {
        try {
            // Convert empty string supervisor_id to undefined
            const submitData = {
                ...data,
                supervisor_id: data.supervisor_id === '' ? undefined : data.supervisor_id,
            };
            await createUser.mutateAsync(submitData);
            enqueueSnackbar('User created successfully', { variant: 'success' });
            navigate('/users');
        } catch (error: any) {
            const message = error.response?.data?.message || 'Failed to create user';
            enqueueSnackbar(message, { variant: 'error' });
        }
    };

    const handleCancel = () => {
        navigate('/users');
    };

    return (
        <PageContainer>
            <PageHeader
                title="Create New Employee"
                description="Add a new employee to the system"
                breadcrumbs={[
                    { label: 'Employees', to: '/users' },
                    { label: 'Create New Employee' },
                ]}
                backTo="/users"
                backLabel="Back to employees"
            />

            {/* Form */}
            <Card>
                <CardHeader>
                    <CardTitle>User Information</CardTitle>
                </CardHeader>
                <CardContent>
                    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Name */}
                            <div className="space-y-2">
                                <Label htmlFor="name">Name *</Label>
                                <Input
                                    id="name"
                                    {...register('name', { required: 'Name is required' })}
                                    className={errors.name ? 'border-destructive' : ''}
                                    placeholder="Enter full name"
                                />
                                {errors.name && (
                                    <p className="text-sm text-destructive">
                                        {errors.name.message}
                                    </p>
                                )}
                            </div>

                            {/* Title */}
                            <div className="space-y-2">
                                <Label htmlFor="title">Job Title</Label>
                                <Input
                                    id="title"
                                    {...register('title')}
                                    placeholder="e.g., Sales Manager, Support Agent"
                                />
                            </div>

                            {/* Email */}
                            <div className="space-y-2">
                                <Label htmlFor="email">Email *</Label>
                                <Input
                                    id="email"
                                    type="email"
                                    {...register('email', {
                                        required: 'Email is required',
                                        pattern: {
                                            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                            message: 'Invalid email address',
                                        },
                                    })}
                                    className={errors.email ? 'border-destructive' : ''}
                                    placeholder="Enter email address"
                                />
                                {errors.email && (
                                    <p className="text-sm text-destructive">
                                        {errors.email.message}
                                    </p>
                                )}
                            </div>

                            {/* Phone */}
                            <div className="space-y-2">
                                <Label htmlFor="phone">Phone Number</Label>
                                <Input
                                    id="phone"
                                    type="text"
                                    {...register('phone')}
                                    placeholder="Enter phone number"
                                />
                            </div>

                            {/* WhatsApp Number */}
                            <div className="space-y-2">
                                <Label htmlFor="whatsapp_number">WhatsApp Number</Label>
                                <Input
                                    id="whatsapp_number"
                                    type="text"
                                    {...register('whatsapp_number')}
                                    placeholder="Enter WhatsApp number"
                                />
                            </div>

                            {/* Telegram */}
                            <div className="space-y-2">
                                <Label htmlFor="telegram">Telegram</Label>
                                <Input
                                    id="telegram"
                                    type="text"
                                    {...register('telegram')}
                                    placeholder="Enter Telegram handle or number"
                                />
                            </div>

                            {/* PBX Extension */}
                            <div className="space-y-2">
                                <Label htmlFor="extension">PBX Extension</Label>
                                <Input
                                    id="extension"
                                    type="text"
                                    {...register('extension')}
                                    placeholder="e.g. 502 (for click-to-call)"
                                />
                            </div>

                            {/* Password */}
                            <div className="space-y-2">
                                <Label htmlFor="password">Password *</Label>
                                <Input
                                    id="password"
                                    type="password"
                                    {...register('password', {
                                        required: 'Password is required',
                                        minLength: {
                                            value: 8,
                                            message: 'Password must be at least 8 characters',
                                        },
                                    })}
                                    className={errors.password ? 'border-destructive' : ''}
                                    placeholder="Enter password"
                                />
                                {errors.password && (
                                    <p className="text-sm text-destructive">
                                        {errors.password.message}
                                    </p>
                                )}
                            </div>

                            {/* Confirm Password */}
                            <div className="space-y-2">
                                <Label htmlFor="password_confirmation">Confirm Password *</Label>
                                <Input
                                    id="password_confirmation"
                                    type="password"
                                    {...register('password_confirmation', {
                                        required: 'Please confirm your password',
                                        validate: (value, formValues) =>
                                            value === formValues.password ||
                                            'Passwords do not match',
                                    })}
                                    className={
                                        errors.password_confirmation ? 'border-destructive' : ''
                                    }
                                    placeholder="Confirm password"
                                />
                                {errors.password_confirmation && (
                                    <p className="text-sm text-destructive">
                                        {errors.password_confirmation.message}
                                    </p>
                                )}
                            </div>

                            {/* Role */}
                            <div className="space-y-2">
                                <Label htmlFor="role">Role *</Label>
                                {rolesLoading ? (
                                    <div className="flex items-center gap-2">
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                        <span className="text-sm text-muted-foreground">
                                            Loading roles...
                                        </span>
                                    </div>
                                ) : (
                                    <Select
                                        value={watch('role_id')}
                                        onValueChange={value => setValue('role_id', value)}
                                    >
                                        <SelectTrigger
                                            className={errors.role_id ? 'border-destructive' : ''}
                                        >
                                            <SelectValue placeholder="Select role" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {roles?.map(role => (
                                                <SelectItem key={role.id} value={role.id}>
                                                    <div className="flex items-center gap-2">
                                                        <span>{role.name}</span>
                                                        <span className="text-xs text-muted-foreground">
                                                            ({role.type})
                                                        </span>
                                                    </div>
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                )}
                                {errors.role_id && (
                                    <p className="text-sm text-destructive">
                                        {errors.role_id.message}
                                    </p>
                                )}
                            </div>

                            {/* Supervisor */}
                            <div className="space-y-2">
                                <Label htmlFor="supervisor">Supervisor</Label>
                                <Select
                                    value={watch('supervisor_id')}
                                    onValueChange={value => setValue('supervisor_id', value)}
                                >
                                    <SelectTrigger>
                                        <SelectValue placeholder="Select supervisor (optional)" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {users.map(user => (
                                            <SelectItem key={user.id} value={user.id}>
                                                <div className="flex items-center gap-2">
                                                    <span>{user.name}</span>
                                                    {user.title && (
                                                        <span className="text-xs text-muted-foreground">
                                                            ({user.title})
                                                        </span>
                                                    )}
                                                </div>
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        {/* Form Actions */}
                        <div className="flex justify-end gap-4 pt-6 border-t">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={handleCancel}
                                disabled={isSubmitting}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                disabled={isSubmitting || rolesLoading}
                                className="gap-2"
                            >
                                {isSubmitting ? (
                                    <>
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                        Creating...
                                    </>
                                ) : (
                                    <>
                                        <Save className="h-4 w-4" />
                                        Create Employee
                                    </>
                                )}
                            </Button>
                        </div>
                    </form>
                </CardContent>
            </Card>
        </PageContainer>
    );
};

export default UserCreatePage;
